
import {
  computed, watch,
  defineComponent, onMounted, onUnmounted, ref, Ref, PropType,
} from 'vue';
import { useRoute } from 'vue-router';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '../store/driveStore';
import { IMedia, ITemporaryFile } from '@/types/interfaces';
import { MEDIA_TYPES } from '@/types/enums';
import MonitoringService from '@/services/MonitoringService';
import AppSkeletonMedia from '@/components/skeletons/AppSkeletonMedia.vue';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import AppNoMessage from '@/components/AppNoMessage.vue';

export default defineComponent({
  props: {
    selector: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
    temporaryFiles: { type: Array as PropType<ITemporaryFile[]> },
  },
  components: {
    AppNoMessage,
    AppSkeletonMedia,
  },
  setup(props) {
    const route = useRoute();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();

    const isNetwork = computed(() => route.meta.isNetwork);
    const files = computed(() => (isNetwork.value ? driveStore.networkFiles : driveStore.files));

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage,
    } = UsePagination();

    const dataLoading: Ref<boolean> = ref(true);
    const itemHeight: Ref<string> = ref('');
    const itemMargin: Ref<string> = ref('');
    const spacer: Ref<HTMLElement | null> = ref(null);
    const noPhotosMessage = computed(() => {
      if (!dataLoading.value && !files.value.length) {
        if (driveStore.search) {
          return {
            text: 'No search results found',
            icon: 'icon-not-found',
          };
        }
        return {
          text: isNetwork.value ? 'Nothing shared with you yet...' : 'No files here yet...',
          icon: 'icon-no-data',
        };
      }
      return '';
    });

    async function fetchData() {
      if (isNetwork.value) {
        return driveStore.fetchNetworkFiles(pageIdx.value);
      }
      return driveStore.fetchFiles(pageIdx.value);
    }

    function handleScroll() {
      onScroll(fetchData, DIRECTION.BOTTOM, dataLoading.value);
    }

    function onResize() {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      itemHeight.value = `${spacer.value!.offsetWidth * 0.7}px`;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      itemMargin.value = `${(spacer.value!.offsetWidth * 5) / 30 + 16}px`;
    }

    function setSelectedStatus() {
      driveStore.files.forEach((file) => {
        if (props.temporaryFiles) {
          // eslint-disable-next-line no-param-reassign
          file.selected = !!props.temporaryFiles!.find((current) => current.id === file.id);
        } else {
          // eslint-disable-next-line no-param-reassign
          file.selected = !!driveStore.currentCase.files.find((current) => current.contentId === file.contentId);
        }
      });
    }

    function selectFile(file: IMedia): void {
      if (!props.selector) {
        return;
      }
      // eslint-disable-next-line no-param-reassign
      file.selected = !file.selected;
    }

    function deleteFile(photoId: string): void {
      driveStore.deleteMedia(photoId, MEDIA_TYPES.FILES);
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        pageIdx.value = 0;
        fetchData().then((response) => {
          setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
        });
      }, 300);
    });

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      window.addEventListener('resize', onResize);
      onResize();
      fetchData()
        .then((response) => {
          setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
        })
        .finally(() => {
          dataLoading.value = false;
          setSelectedStatus();
        });
    });

    onUnmounted(() => {
      window.removeEventListener('resize', onResize);
    });

    return {
      mainStore,
      driveStore,
      dataLoading,
      itemHeight,
      itemMargin,
      spacer,
      noPhotosMessage,
      handleScroll,
      isPageLoading,
      scroller,
      selectFile,
      deleteFile,
      files,
      isNetwork,
    };
  },
});
